import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from './ui/notify';
import { apiCall, getApiBaseUrl, authHeaders, withToken } from './config';
import DigitalWorkContract from './DigitalWorkContract';
import { useTranslation } from './i18n/LanguageContext';

function DocumentManagement({ user }) {
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [file, setFile] = useState(null);
  const [type, setType] = useState('vertrag');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  // Nicht-Admins (Mitarbeiter, Einsatzleitung, Stations-Planer) starten bei
  // „Mein Arbeitsvertrag" – harte 'employee'-Checks ließen die neuen Rollen
  // sonst vor leeren Tabs stehen.
  const [activeTab, setActiveTab] = useState(user.role !== 'admin' ? 'my-contract' : 'documents'); // documents, contract-template, my-contract, submitted-contracts
  const [contractTemplate, setContractTemplate] = useState(null);
  const [employeeContract, setEmployeeContract] = useState(null);
  const [submittedContracts, setSubmittedContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  // Hinterlegte Arbeitgeber-Unterschrift (wird bei Genehmigung automatisch eingefügt)
  const [employerSig, setEmployerSig] = useState(null);
  const loadEmployerSig = async () => {
    try {
      const r = await apiCall('/admin/employer-signature');
      setEmployerSig(r && r.signature ? r.signature : null);
    } catch (_) { setEmployerSig(null); }
  };
  const handleEmployerSigUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { notify('Bitte ein Bild (PNG/JPG) auswählen'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const applyToApproved = await confirmDialog(
        'Unterschrift auch rückwirkend in alle bereits genehmigten Verträge einfügen, die noch keine haben?',
        { title: 'Arbeitgeber-Unterschrift', confirmText: 'Ja, auch rückwirkend', cancelText: 'Nur für künftige Genehmigungen' }
      );
      try {
        const r = await apiCall('/admin/employer-signature', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ signature: reader.result, applyToApproved }),
        });
        notify(`Unterschrift gespeichert${applyToApproved ? ` – in ${r.applied || 0} genehmigte Verträge eingefügt` : ''}`);
        loadEmployerSig();
      } catch (err) {
        notify('Fehler beim Speichern der Unterschrift');
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  const [showSuccessMessage, setShowSuccessMessage] = useState('');

  const { t } = useTranslation();

  const documentTypes = {
    'arbeitsvertrag': t('doc.typeContract'),
    'vertrag': t('doc.typeContract'), // Fallback für alte Einträge
    'verfugbarkeit': t('doc.typeAvailability'),
    'abrechnung': t('doc.typePayslip'),
    'aufenthaltstitel': t('doc.typeResidencePermit'),
    'arbeitsgenehmigung': t('doc.typeWorkPermit'),
    'sonstiges': t('doc.typeOther')
  };

  useEffect(() => {
    fetchDocuments();
    if (user.role === 'admin') {
      fetchUsers();
      loadContractTemplate();
      if (activeTab === 'submitted-contracts') {
        fetchSubmittedContracts();
        loadEmployerSig();
      }
    }
    if (user.role !== 'admin' && activeTab === 'my-contract') {
      loadContractTemplate(); // Lade auch die Vorlage für Mitarbeiter
      loadEmployeeContract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUser, activeTab]);

  const loadContractTemplate = async () => {
    try {
      const response = await apiCall('/contract-template');
      if (response.ok) {
        const template = response;
        
        // Konvertiere Backend-Format zu Frontend-Format
        if (template && template.position) {
          const frontendTemplate = {
            employerName: template.employer?.name || 'Bavarian Concept Group GmbH',
            employerAddress: template.employer?.address || 'Bürkelweg 18\n85540 Haar',
            position: template.position?.title || 'Unterstützung bei der Gepäckaufbewahrung auf dem Oktoberfest in München',
            startDate: template.position?.startDate || '',
            endDate: template.position?.endDate || '',
            salary: template.salary?.amount || '15',
            salaryType: template.salary?.type || 'stundenlohn',
            workingTime: template.salary?.workingTime || 
                        (typeof template.workingTime?.regular === 'string' ? template.workingTime.regular : 'durchschnittlich 8 Stunden pro Tag'),
            breakTime: typeof template.workingTime?.breaks === 'string' ? template.workingTime.breaks : 
                      'Bei einer Arbeitszeit von mehr als 6 Stunden steht eine Pause von 30 Minuten zu.',
            vacationDays: typeof template.workingTime?.vacation === 'string' ? template.workingTime.vacation : 
                         'Bei dieser kurzfristigen Beschäftigung entsteht kein Urlaubsanspruch.',
            specialAgreements: template.documents?.specialAgreements || 'Der Arbeitnehmer verpflichtet sich, über alle betrieblichen Angelegenheiten Stillschweigen zu bewahren.',
            kurzfristigeDetails: template.documents?.kurzfristigeDetails || 'Der Arbeitnehmer versichert dem Arbeitgeber im laufenden Kalenderjahr keine kurzfristige Beschäftigung ausgeübt zu haben, durch die die Grenze von drei Monaten oder 70 Arbeitstagen überschritten wird. Er versichert weiter, dass diese Grenze auch durch das vorliegende Arbeitsverhältnis nicht überschritten wird.',
            verfallsfristen: template.documents?.verfallsfristen || 'Ansprüche aus diesem Arbeitsverhältnis sind innerhalb einer Frist von drei Monaten nach Fälligkeit in Textform gegenüber der anderen Vertragspartei geltend zu machen. Ansonsten gelten die Ansprüche als verwirkt. Bei einer Ablehnung der Gegenseite oder einer fehlenden Erklärung dieser zwei Wochen nach Geltendmachung kommt es ebenfalls zum Verfall des Anspruchs, wenn keine gerichtliche Geltendmachung stattfindet. Dabei gilt eine einmonatige Frist nach Ablehnung oder Fristablauf.',
            vertragsaenderungen: template.documents?.vertragsaenderungen || 'Änderungen, Ergänzungen und Nebenabreden bedürfen der Schriftform; dies gilt auch für die Aufhebung der Schriftform selbst. Sollten einzelne Bestimmungen dieses Vertrages unwirksam sein oder werden, wird hierdurch die Wirksamkeit des Vertrages im Übrigen nicht berührt.'
          };
          setContractTemplate(frontendTemplate);
        } else {
          setContractTemplate(template);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Vertragsvorlage:', error);
    }
  };

  const saveContractTemplate = async (templateData) => {
    try {
      console.log('Saving template data:', templateData); // Debug log
      
      // Strukturiere die Daten für das Backend
      const payload = {
        employer: {
          name: templateData.employerName,
          address: templateData.employerAddress
        },
        position: {
          title: templateData.position,
          startDate: templateData.startDate,
          endDate: templateData.endDate
        },
        salary: {
          amount: templateData.salary,
          type: templateData.salaryType,
          workingTime: templateData.workingTime
        },
        workingTime: {
          regular: templateData.workingTime,
          breaks: templateData.breakTime,
          vacation: templateData.vacationDays
        },
        documents: {
          specialAgreements: templateData.specialAgreements,
          kurzfristigeDetails: templateData.kurzfristigeDetails,
          verfallsfristen: templateData.verfallsfristen,
          vertragsaenderungen: templateData.vertragsaenderungen
        }
      };

      console.log('Sending payload to backend:', payload); // Debug log

      const response = await apiCall('/contract-template', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        const saved = response;
        setContractTemplate(saved);
        showSuccess(t('doc.templateSaved'));
      } else {
        throw new Error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Vorlage:', error);
      throw error;
    }
  };

  const showSuccess = (message) => {
    setShowSuccessMessage(message);
    setTimeout(() => setShowSuccessMessage(''), 3000);
  };

  const showError = (message) => {
    notify(message); // Fallback für Fehlermeldungen
  };

  const loadEmployeeContract = async () => {
    try {
      const contracts = await apiCall(`/employee-contracts/history/${user.id}`);
      if (Array.isArray(contracts) && contracts.length > 0) {
        // Nur den Vertrag des AKTUELLEN Jahres laden -> neue Saison startet clean,
        // solange für dieses Jahr noch kein Vertrag existiert.
        const currentYear = new Date().getFullYear();
        const currentContract = contracts.find(c => Number(c.year) === currentYear);
        if (!currentContract) {
          setEmployeeContract(null);
          return;
        }
        if (currentContract.contract_data && typeof currentContract.contract_data === 'string') {
          try {
            currentContract.contract_data = JSON.parse(currentContract.contract_data);
          } catch (e) {
            console.error('Fehler beim Parsen der Vertragsdaten:', e);
          }
        }
        setEmployeeContract(currentContract);
      } else {
        setEmployeeContract(null);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Mitarbeitervertrags:', error);
    }
  };

  const saveEmployeeContract = async (contractData, status = 'draft', templateOptions = {}) => {
    try {
      // Validierung der User ID
      if (!user || !user.id) {
        console.error('User oder User ID fehlt:', user);
        throw new Error('User ID ist erforderlich. Bitte loggen Sie sich erneut ein.');
      }

      console.log('Speichere Vertrag für User ID:', user.id);
      const saved = await apiCall('/employee-contracts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,  // Backend erwartet userId
          contractData,
          status,
          saveAsTemplate: templateOptions.saveAsTemplate || false,
          deleteTemplate: templateOptions.deleteTemplate || false
        }),
      });
      
      setEmployeeContract({ ...saved, contract_data: contractData, status: status });
      // Beim stillen Auto-Speichern: kein Toast, kein Reload (würde das Ausfüllen stören)
      if (templateOptions.silent) {
        return saved;
      }
      // Vertrag neu laden um konsistenten State zu haben
      loadEmployeeContract();
      if (status === 'submitted') {
        showSuccess(t('doc.contractSubmitted'));
      } else {
        showSuccess(t('doc.contractDraft'));
      }
      return saved;
    } catch (error) {
      console.error('Fehler beim Speichern des Arbeitsvertrags:', error);
      throw error;
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await apiCall('/users');
      console.log('Loaded users:', data); // Debug-Ausgabe
      setUsers(data.filter(u => u.role === 'employee' || u.role_id === 2));
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer:', err);
    }
  };

  const fetchSubmittedContracts = async () => {
    console.log('📋 Lade eingereichte Verträge...');
    try {
      const contracts = await apiCall('/employee-contracts');
      console.log('✅ Eingereichte Verträge geladen:', contracts);
      
      // Sicherstellen, dass contracts ein Array ist
      if (Array.isArray(contracts)) {
        // Schnell-Liste: Der Server liefert nur noch Vorhanden-Flags statt der
        // riesigen contract_data (Base64-Ausweise). Für die Tabellen-Badges bauen
        // wir daraus ein Pseudo-contract_data; die vollen Daten lädt viewContract.
        const parsedContracts = contracts.map(contract => ({
          ...contract,
          contract_data: {
            employeeNationality: contract.nationality || null,
            idCardImage: contract.has_id_card ? true : null,
            workPermitDocument: contract.has_work_permit ? true : null,
            residencePermitDocument: contract.has_residence_permit ? true : null,
            employeeSignature: contract.has_employee_signature ? true : null,
            parentSignature: contract.has_parent_signature ? true : null,
            employerSignature: contract.has_employer_signature ? true : null,
            __slim: true
          }
        }));
        setSubmittedContracts(parsedContracts);
      } else {
        console.warn('API returned non-array data:', contracts);
        setSubmittedContracts([]);
      }
    } catch (err) {
      console.error('Fehler beim Laden der eingereichten Verträge:', err);
      setSubmittedContracts([]); // Fallback zu leerem Array
    }
  };

  // Vollen Vertrag (inkl. Ausweisfotos) erst beim Öffnen nachladen
  const loadFullContract = async (contractId) => {
    const full = await apiCall(`/employee-contracts/${contractId}`);
    if (full.contract_data && typeof full.contract_data === 'string') {
      try {
        full.contract_data = JSON.parse(full.contract_data);
      } catch (e) {
        console.error('Fehler beim Parsen der contract_data:', e);
        full.contract_data = null;
      }
    }
    return full;
  };

  const viewContract = async (contract) => {
    try {
      const full = await loadFullContract(contract.id);
      setSelectedContract(full);
      setShowContractModal(true);
    } catch (error) {
      console.error('Fehler beim Laden des Vertrags:', error);
      showError(t('doc.contractNotFound'));
    }
  };

  // Ablehnen mit Grund: fragt den Grund ab (wird dem Mitarbeiter in Web-App,
  // E-Mail und WhatsApp-Bot angezeigt). Liefert false, wenn abgebrochen wurde.
  const rejectContractWithReason = async (contractId) => {
    const reason = window.prompt(
      'Grund der Ablehnung (wird dem Mitarbeiter angezeigt):\n' +
      'z.B. "Arbeitserlaubnis fehlt", "Unterschrift fehlt", "Ausweis unlesbar"',
      ''
    );
    if (reason === null) return false; // abgebrochen → Status bleibt
    await updateContractStatus(contractId, 'rejected', reason.trim());
    return true;
  };

  const updateContractStatus = async (contractId, status, reason = null) => {
    try {
      const result = await apiCall(`/employee-contracts/${contractId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, reason }),
      });
      
      if (status === 'approved') {
        if (result.documentCreated && result.pdfPath) {
          showSuccess(t('doc.contractApprovedWithPdf'));
          // Aktualisiere auch die Dokumentenliste für den Fall, dass der Admin sie offen hat
          fetchDocuments();
        } else if (result.documentCreated) {
          showSuccess(t('doc.contractApprovedWithDoc'));
          // Aktualisiere auch die Dokumentenliste für den Fall, dass der Admin sie offen hat
          fetchDocuments();
        } else if (result.warning) {
          showSuccess(t('doc.contractApprovedBut', { warning: result.warning }));
        } else {
          showSuccess(t('doc.contractApproved'));
        }
      } else {
        showSuccess(t(status === 'rejected' ? 'doc.contractRejected' : 'doc.contractUpdated'));
      }
      
      fetchSubmittedContracts(); // Aktualisiere die Liste
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Vertragsstatus:', error);
      showError(t('doc.errorUpdateStatus'));
    }
  };

  const generateContractPDF = async (contractId) => {
    try {
      console.log('Generating PDF for contract:', contractId);

      // Volle Vertragsdaten nachladen (die Liste hält nur noch die Schnell-Version)
      const contract = await loadFullContract(contractId);
      if (!contract) {
        showError(t('doc.contractNotFound'));
        return;
      }

      // Erstelle eine temporäre Instanz des Vertrags für das PDF
      showSuccess(t('doc.pdfGenerating'));
      
      // Öffne Modal und zeige Vertrag an
      setSelectedContract(contract);
      setShowContractModal(true);
      
      // Warte kurz bis Modal gerendert ist, dann generiere PDF
      setTimeout(async () => {
        try {
          await generatePDFFromContract(contract);
          showSuccess(t('doc.pdfCreated'));
        } catch (error) {
          console.error('PDF generation error:', error);
          showError(t('doc.errorPdfCreation'));
        }
      }, 1000);
      
    } catch (error) {
      console.error('Fehler beim Generieren des PDFs:', error);
      showError(t('doc.errorPdfGeneration'));
    }
  };

  // PDF im Browser erzeugen (identisch zu „PDF erzeugen") und OHNE Download beim
  // Mitarbeiter als Dokument ablegen – genutzt bei Genehmigung und Sammel-Erzeugung.
  const storeContractPdf = async (contractId) => {
    const contract = await loadFullContract(contractId);
    if (!contract) throw new Error('Vertrag nicht gefunden');
    setSelectedContract(contract);
    setShowContractModal(true);
    await new Promise((r) => setTimeout(r, 1200)); // Modal rendern lassen
    try {
      await generatePDFFromContract(contract, { download: false });
    } finally {
      setShowContractModal(false);
    }
  };

  // Genehmigen + Browser-PDF automatisch ablegen
  const approveWithPdf = async (contractId) => {
    await updateContractStatus(contractId, 'approved');
    try {
      await storeContractPdf(contractId);
      showSuccess('Vertrag genehmigt – PDF beim Mitarbeiter abgelegt.');
    } catch (e) {
      console.error('PDF-Ablage nach Genehmigung fehlgeschlagen:', e);
      showError('Genehmigt, aber das PDF konnte nicht erzeugt werden – bitte „PDF erzeugen" im Vertrag nutzen.');
    }
  };

  // Alle genehmigten Verträge des Jahres nacheinander als Browser-PDF ablegen
  const [bulkPdfProgress, setBulkPdfProgress] = useState(null);
  const regenerateAllPdfs = async () => {
    const targets = (submittedContracts || []).filter((c) => c.status === 'approved' && Number(c.year) === new Date().getFullYear());
    if (!targets.length) { notify('Keine genehmigten Verträge für dieses Jahr'); return; }
    if (!await confirmDialog(`Für ${targets.length} genehmigte Verträge das PDF neu erzeugen und beim Mitarbeiter ablegen? Dauert ca. ${Math.ceil(targets.length * 2.5 / 60)} Min. – bitte den Browser dabei offen lassen.`, { title: 'PDFs neu erzeugen', confirmText: 'Starten' })) return;
    let ok = 0;
    for (let i = 0; i < targets.length; i++) {
      setBulkPdfProgress(`${i + 1}/${targets.length}: ${targets[i].firstname} ${targets[i].lastname}`);
      try { await storeContractPdf(targets[i].id); ok++; } catch (e) { console.error('PDF fehlgeschlagen:', targets[i].id, e); }
    }
    setBulkPdfProgress(null);
    showSuccess(`${ok} von ${targets.length} PDFs erzeugt und abgelegt.`);
    fetchDocuments();
  };

  const generatePDFFromContract = async (contract, opts = {}) => {
    // jsPDF + html2canvas erst bei Bedarf laden (Code-Splitting: ~120 KB raus aus dem Initial-Bundle)
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas'),
    ]);

    // Warte bis das Modal vollständig gerendert ist
    const modalElement = document.querySelector('.modal-body');
    if (!modalElement) {
      throw new Error('Modal nicht gefunden');
    }

    // Temporär CSS für bessere PDF-Darstellung hinzufügen
    const style = document.createElement('style');
    style.textContent = `
      /* Interne Blöcke (Status, Verwaltungsinfos, Hinweise) gehören nicht ins Vertrags-PDF;
         .pdf-only-Elemente (z.B. Kopfzeile "Arbeitsvertrag – Bavarian Concept Group") nur dort */
      .modal-body .no-pdf { display: none !important; }
      .modal-body .pdf-only { display: inline !important; }
      .modal-body,
      .modal-body *,
      .modal-body p,
      .modal-body div,
      .modal-body span,
      .modal-body input,
      .modal-body textarea,
      .modal-body label,
      .modal-body small,
      .modal-body .form-control-plaintext,
      .modal-body .text-muted {
        color: #000000 !important;
        background-color: transparent !important;
        opacity: 1 !important;
        font-weight: normal !important;
      }
      .modal-body {
        background-color: #ffffff !important;
        font-weight: normal !important;
      }
      .modal-body h1, .modal-body h2, .modal-body h3, .modal-body h4, .modal-body h5, .modal-body h6 {
        color: #000000 !important;
        font-weight: bold !important;
        opacity: 1 !important;
      }
      .modal-body strong, .modal-body b {
        color: #000000 !important;
        font-weight: bold !important;
        opacity: 1 !important;
      }
      .modal-body .form-control-plaintext {
        color: #000000 !important;
        border-color: #000000 !important;
        opacity: 1 !important;
      }
      .modal-body input[readonly] {
        color: #000000 !important;
        background-color: #ffffff !important;
        border: 1px solid #000000 !important;
        opacity: 1 !important;
      }
    `;
    document.head.appendChild(style);

    // Warte kurz damit CSS wirkt
    await new Promise(resolve => setTimeout(resolve, 200));

    // Umbruch-Kandidaten VOR dem Rendern einsammeln (CSS-Pixel relativ zum Modal-Anfang):
    // Unterkanten von Absätzen/Zeilen = erlaubte Schnittstellen, Oberkanten von
    // Überschriften = „hier lieber VOR der Überschrift umbrechen".
    const modalTop = modalElement.getBoundingClientRect().top;
    const isHidden = (el) => { const cs = getComputedStyle(el); return cs.display === 'none' || cs.visibility === 'hidden'; };
    const breakBottoms = [];
    modalElement.querySelectorAll('p, li, tr, label, small, h1, h2, h3, h4, h5, h6, .form-control-plaintext, .signature-box, .alert, img').forEach((el) => {
      if (isHidden(el)) return;
      const r = el.getBoundingClientRect();
      if (r.height > 0) breakBottoms.push(r.bottom - modalTop);
    });
    const headingTops = [];
    modalElement.querySelectorAll('h1, h2, h3, h4, h5, h6, strong').forEach((el) => {
      if (isHidden(el)) return;
      const r = el.getBoundingClientRect();
      if (r.height > 0) headingTops.push(r.top - modalTop);
    });

    // Erstelle Canvas vom Modal-Inhalt mit optimierten Einstellungen
    const canvas = await html2canvas(modalElement, {
      scale: 2, // Reduzierte Auflösung für kleinere Dateien
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      removeContainer: false,
      foreignObjectRendering: false, // Deaktiviere foreign object rendering
      ignoreElements: (element) => {
        // Ignoriere spezielle Elemente die Probleme verursachen könnten
        return element.classList.contains('modal-backdrop') || 
               element.classList.contains('modal-header') ||
               element.tagName === 'BUTTON';
      },
      onclone: (clonedDoc) => {
        // Stelle sicher dass alle Texte schwarz sind im geklonten DOM
        const allElements = clonedDoc.querySelectorAll('*');
        allElements.forEach(el => {
          // Forciere schwarze Farbe für alle Textelemente
          if (el.nodeType === Node.ELEMENT_NODE) {
            el.style.setProperty('color', '#000000', 'important');
            el.style.setProperty('opacity', '1', 'important');
            el.style.setProperty('background-color', 'transparent', 'important');
            
            // Spezielle Behandlung für Inputs
            if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
              el.style.setProperty('color', '#000000', 'important');
              el.style.setProperty('background-color', '#ffffff', 'important');
              el.style.setProperty('border-color', '#000000', 'important');
            }
            
            // Entferne text-muted Klassen
            if (el.classList.contains('text-muted')) {
              el.classList.remove('text-muted');
              el.style.setProperty('color', '#000000', 'important');
            }
          }
        });
        
        // Setze auch den Body des geklonten Dokuments
        clonedDoc.body.style.setProperty('color', '#000000', 'important');
        clonedDoc.body.style.setProperty('background-color', '#ffffff', 'important');
      }
    });

    // Entferne temporäres CSS
    document.head.removeChild(style);

    // PDF mit INTELLIGENTEN Seitenumbrüchen: Das Canvas wird nur an Absatz-/
    // Zeilengrenzen zerschnitten; steht kurz vor dem Schnitt eine Überschrift,
    // wandert sie komplett auf die nächste Seite. Kleiner Rand oben/unten.
    const pdf = new jsPDF();
    const imgWidth = 210;            // A4-Breite in mm
    const pageHeightMm = 297;
    const marginMm = 8;              // oben/unten
    const usableMm = pageHeightMm - 2 * marginMm;
    const scale = canvas.width / modalElement.getBoundingClientRect().width; // CSS-px → Canvas-px
    const pxPerMm = canvas.width / imgWidth;
    const pageHeightPx = usableMm * pxPerMm;
    const minPagePx = pageHeightPx * 0.55;   // nie extrem kurze Seiten erzeugen
    const bottoms = breakBottoms.map((v) => v * scale).sort((a, b) => a - b);
    const heads = headingTops.map((v) => v * scale).sort((a, b) => a - b);

    let start = 0;
    let first = true;
    while (start < canvas.height - 2) {
      let end = Math.min(start + pageHeightPx, canvas.height);
      if (end < canvas.height) {
        // größte erlaubte Schnittstelle innerhalb der Seite
        let best = null;
        for (const b of bottoms) { if (b > start + minPagePx && b <= end) best = b; }
        if (best !== null) end = best;
        // Überschrift kurz vor dem Schnitt? → davor umbrechen
        const guard = 90 * scale;
        for (const h of heads) {
          if (h > end - guard && h < end + 2 && h - 6 * scale > start + minPagePx) { end = h - 6 * scale; break; }
        }
      }
      const sliceH = Math.max(1, Math.round(end - start));
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceH;
      pageCanvas.getContext('2d').drawImage(canvas, 0, Math.round(start), canvas.width, sliceH, 0, 0, canvas.width, sliceH);
      const img = pageCanvas.toDataURL('image/jpeg', 0.85);
      if (!first) pdf.addPage();
      pdf.addImage(img, 'JPEG', 0, marginMm, imgWidth, sliceH / pxPerMm);
      first = false;
      start = end;
    }

    // Erstelle Dateiname
    const employeeName = contract.employee_name || 'Unbekannt';
    const formattedName = employeeName.trim().replace(/[^a-zA-ZäöüÄÖÜß\s]/g, '').replace(/\s+/g, '_');
    const fileName = `${formattedName}_Arbeitsvertrag_${new Date().toISOString().split('T')[0]}.pdf`;

    // Download nur, wenn gewünscht (bei Genehmigung/Sammel-Erzeugung nicht)
    if (opts.download !== false) pdf.save(fileName);

    // Immer ans Backend: wird als Dokument „Arbeitsvertrag" beim Mitarbeiter abgelegt
    await sendPDFToBackend(contract.id, pdf.output('blob'), fileName);
  };

  const sendPDFToBackend = async (contractId, pdfBlob, fileName) => {
    try {
      const formData = new FormData();
      formData.append('pdf', pdfBlob, fileName);
      formData.append('contractId', contractId);

      const result = await fetch(`/api/employee-contracts/${contractId}/save-pdf`, {
        method: 'POST',
        body: formData,
        headers: { ...authHeaders() },
      });

      if (result.ok) {
        console.log('PDF erfolgreich an Backend gesendet');
        fetchDocuments(); // Aktualisiere Dokumentenliste
      } else {
        throw new Error(`Upload fehlgeschlagen (${result.status})`);
      }
    } catch (error) {
      console.error('Fehler beim Senden an Backend:', error);
    }
  };

  const deleteEmployeeContract = async (contractId) => {
    if (!await confirmDialog(t('doc.confirmContractDelete'))) {
      return;
    }
    
    try {
      console.log('🗑️ Deleting contract:', contractId);
      await apiCall(`/employee-contracts/${contractId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      showSuccess(t('doc.contractDeleted'));
      fetchSubmittedContracts(); // Aktualisiere die Liste
    } catch (error) {
      console.error('Fehler beim Löschen des Vertrags:', error);
      showError(t('doc.errorDeleteContract'));
    }
  };

  const fetchDocuments = async () => {
    try {
      let url = '/documents';
      const params = new URLSearchParams();
      
      if (user.role === 'admin') {
        params.append('userRole', 'admin');
        if (filterUser) {
          params.append('userId', filterUser);
        }
      } else {
        params.append('userId', user.id);
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const data = await apiCall(url);
      setDocuments(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Dokumente:', err);
      setDocuments([]); // Fallback auf leeres Array
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    const targetUserId = user.role === 'admin' ? selectedUserId : user.id;
    if (!targetUserId) {
      notify(t('doc.selectEmployee'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', type);
      formData.append('user_id', targetUserId);

      const res = await apiCall('/documents/upload', {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        setFile(null);
        setSelectedUserId('');
        setType('vertrag');
        await fetchDocuments();
        showSuccess(t('doc.uploaded'));
      } else {
        const error = res;
        notify(t('doc.errorUpload') + ': ' + error.error);
      }
    } catch (err) {
      console.error('Upload-Fehler:', err);
      notify(t('doc.errorUpload'));
    }
    setUploading(false);
  };

  const handleDelete = async (id, filename) => {
    if (await confirmDialog(t('doc.confirmDelete', { name: filename }))) {
      try {
        const res = await apiCall(`/documents/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: user.id,
            user_role: user.role
          })
        });
        if (res.ok) {
          await fetchDocuments();
          showSuccess(t('doc.deleted'));
        } else {
          const error = res;
          notify(t('doc.errorDelete') + ': ' + (error.error || t('doc.unknownError')));
        }
      } catch (err) {
        console.error('Fehler beim Löschen:', err);
        notify(t('doc.errorDeleteDoc'));
      }
    }
  };

  const handleViewDocument = async (docId, userId = null) => {
    try {
      // Sichere Dokument-Anzeige wie in UserManagement
      const baseUrl = getApiBaseUrl();
      
      // Verwende user.id (aktueller eingeloggter User) oder übergebene userId
      const currentUserId = userId || user.id;
      
      if (!currentUserId) {
        notify(t('doc.errorNoUserId'));
        return;
      }
      
      const documentUrl = `${baseUrl}/documents/${docId}/view?user_id=${currentUserId}`;
      
      console.log('Opening document URL:', documentUrl);
      
      // Erst prüfen ob die URL erreichbar ist (mit Token)
      const response = await fetch(documentUrl, { method: 'HEAD', headers: authHeaders() });
      if (response.ok) {
        // URL mit Token öffnen (window.open kann keinen Header senden)
        window.open(withToken(documentUrl), '_blank');
      } else {
        console.error('Document not accessible:', response.status, response.statusText);
        if (response.status === 401) {
          notify(t('doc.errorUnauthorized'));
        } else if (response.status === 403) {
          notify(t('doc.errorForbidden'));
        } else {
          notify(t('doc.errorViewDoc') + ': ' + response.status);
        }
      }
    } catch (error) {
      console.error('Fehler beim Anzeigen des Dokuments:', error);
      notify(t('doc.errorViewDoc') + ': ' + error.message);
    }
  };

  const handleStatusChange = async (docId, newStatus) => {
    try {
      const res = await apiCall(`/documents/${docId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        await fetchDocuments();
        showSuccess(t('doc.statusChanged', { status: getStatusLabel(newStatus) }));
      } else {
        const error = res;
        notify(t('doc.errorChangeStatus') + ': ' + error.error);
      }
    } catch (err) {
      console.error('Status-Änderungs-Fehler:', err);
      notify(t('doc.errorChangeStatus'));
    }
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'ausstehend': t('doc.statusPending'),
      'genehmigt': t('doc.statusApproved'),
      'abgelehnt': t('doc.statusRejected')
    };
    return statusLabels[status] || status;
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'genehmigt': return 'bg-success';
      case 'abgelehnt': return 'bg-danger';
      case 'ausstehend': 
      default: return 'bg-warning';
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2><i className="bi bi-file-earmark-text me-2"></i>{t('doc.title')}</h2>
          </div>

          {/* Success Message */}
          {showSuccessMessage && (
            <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
              <i className="bi bi-check-circle me-2"></i>
              {showSuccessMessage}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setShowSuccessMessage('')}
              ></button>
            </div>
          )}

          {/* Tab Navigation */}
          {user.role === 'admin' ? (
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'documents' ? 'active' : ''}`}
                  onClick={() => setActiveTab('documents')}
                >
                  <i className="bi bi-file-earmark me-2"></i>{t('doc.tabDocuments')}
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'contract-template' ? 'active' : ''}`}
                  onClick={() => setActiveTab('contract-template')}
                >
                  <i className="bi bi-file-earmark-text me-2"></i>{t('doc.tabTemplate')}
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'submitted-contracts' ? 'active' : ''}`}
                  onClick={() => setActiveTab('submitted-contracts')}
                >
                  <i className="bi bi-file-earmark-check me-2"></i>{t('doc.tabSubmitted')}
                  {Array.isArray(submittedContracts) && submittedContracts.filter(c => c.status === 'submitted').length > 0 && (
                    <span className="badge bg-primary ms-2">
                      {submittedContracts.filter(c => c.status === 'submitted').length}
                    </span>
                  )}
                </button>
              </li>
            </ul>
          ) : (
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'my-contract' ? 'active' : ''}`}
                  onClick={() => setActiveTab('my-contract')}
                >
                  <i className="bi bi-file-earmark-text me-2"></i>{t('doc.tabMyContract')}
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'documents' ? 'active' : ''}`}
                  onClick={() => setActiveTab('documents')}
                >
                  <i className="bi bi-file-earmark me-2"></i>{t('doc.tabMyDocs')}
                </button>
              </li>
            </ul>
          )}

          {/* Content basierend auf aktivem Tab */}
          {activeTab === 'documents' && (
            <>
              {/* Upload-Bereich */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5><i className="bi bi-cloud-upload me-2"></i>{t('doc.upload')}</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleUpload}>
                    <div className="row">
                      {user.role === 'admin' && (
                        <div className="col-md-3 mb-3">
                          <label className="form-label">{t('doc.employee')} *</label>
                          <select 
                            className="form-select"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            required
                          >
                            <option value="">{t('doc.select')}</option>
                            {users.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.firstname && u.lastname ? `${u.firstname} ${u.lastname}` : 
                                 u.firstname ? u.firstname : 
                                 u.lastname ? u.lastname : 
                                 `Benutzer ID: ${u.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      <div className="col-md-3 mb-3">
                        <label className="form-label">{t('doc.docType')}</label>
                        <select 
                          className="form-select"
                          value={type}
                          onChange={(e) => setType(e.target.value)}
                        >
                          {Object.entries(documentTypes).map(([key, name]) => (
                            <option key={key} value={key}>{name}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t('doc.selectFile')} *</label>
                        <input 
                          type="file"
                          className="form-control"
                          onChange={(e) => setFile(e.target.files[0])}
                          required
                        />
                      </div>
                      
                      <div className="col-md-2 mb-3 d-flex align-items-end">
                        <button 
                          type="submit"
                          className="btn btn-primary w-100"
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              {t('doc.uploading')}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-upload me-2"></i>
                              {t('doc.uploadBtn')}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Filter (nur für Admin) */}
              {user.role === 'admin' && (
                <div className="card mb-4">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <label className="form-label">{t('doc.filterEmployee')}</label>
                        <select 
                          className="form-select"
                          value={filterUser}
                          onChange={(e) => setFilterUser(e.target.value)}
                        >
                          <option value="">{t('doc.allEmployees')}</option>
                          {users.map(u => (
                            <option key={u.id} value={u.id}>
                              {u.firstname && u.lastname ? `${u.firstname} ${u.lastname}` : 
                               u.firstname ? u.firstname : 
                               u.lastname ? u.lastname : 
                               `Benutzer ID: ${u.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Dokumenten-Liste */}
              <div className="card">
                <div className="card-header">
                  <h5><i className="bi bi-files me-2"></i>{t('doc.overview')}</h5>
                </div>
                <div className="card-body">
                  {documents.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="bi bi-file-earmark-x display-1 text-muted"></i>
                      <p className="text-muted mt-2">{t('doc.noDocuments')}</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-dark">
                          <tr>
                            {user.role === 'admin' && <th>{t('doc.employee')}</th>}
                            <th>{t('doc.filename')}</th>
                            <th className="d-none d-md-table-cell">{t('doc.type')}</th>
                            <th className="d-none d-lg-table-cell">{t('doc.status')}</th>
                            <th>{t('doc.date')}</th>
                            <th>{t('doc.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documents.map(doc => (
                            <tr key={doc.id}>
                              {user.role === 'admin' && (
                                <td>
                                  <span className="badge bg-info">
                                    {doc.user_name || doc.lastname || 'Unbekannt'}
                                  </span>
                                </td>
                              )}
                              <td>
                                <div>
                                  <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                                  <span className="d-none d-md-inline">
                                    {doc.original_name || doc.filename}
                                  </span>
                                  <span className="d-md-none">
                                    {(doc.original_name || doc.filename)?.length > 20 
                                      ? (doc.original_name || doc.filename).substring(0, 20) + '...'
                                      : (doc.original_name || doc.filename)}
                                  </span>
                                  {/* Mobile: Typ als kleines Badge unter dem Dateinamen */}
                                  <div className="d-md-none mt-1">
                                    <span className={`badge badge-sm ${
                                      doc.document_type === 'arbeitsvertrag' || doc.document_type === 'vertrag' || doc.type === 'vertrag' ? 'bg-success' :
                                      doc.document_type === 'verfugbarkeit' || doc.type === 'verfugbarkeit' ? 'bg-warning' :
                                      doc.document_type === 'abrechnung' || doc.type === 'abrechnung' ? 'bg-danger' : 'bg-secondary'
                                    }`} style={{ fontSize: '0.7rem' }}>
                                      {documentTypes[doc.document_type || doc.type] || doc.document_type || doc.type}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="d-none d-md-table-cell">
                                <span className={`badge ${
                                  doc.document_type === 'arbeitsvertrag' || doc.document_type === 'vertrag' || doc.type === 'vertrag' ? 'bg-success' :
                                  doc.document_type === 'verfugbarkeit' || doc.type === 'verfugbarkeit' ? 'bg-warning' :
                                  doc.document_type === 'abrechnung' || doc.type === 'abrechnung' ? 'bg-danger' : 'bg-secondary'
                                }`}>
                                  {documentTypes[doc.document_type || doc.type] || doc.document_type || doc.type}
                                </span>
                              </td>
                              <td className="d-none d-lg-table-cell">
                                {user.role === 'admin' ? (
                                  <select 
                                    className={`form-select form-select-sm ${getStatusBadgeClass(doc.status)} text-white`}
                                    value={doc.status || 'ausstehend'}
                                    onChange={(e) => handleStatusChange(doc.id, e.target.value)}
                                    style={{ width: '140px' }}
                                  >
                                    <option value="ausstehend">📝 {t('doc.statusPending')}</option>
                                    <option value="genehmigt">✅ {t('doc.statusApproved')}</option>
                                    <option value="abgelehnt">❌ {t('doc.statusRejected')}</option>
                                  </select>
                                ) : (
                                  <span className={`badge ${getStatusBadgeClass(doc.status || 'ausstehend')}`}>
                                    {getStatusLabel(doc.status || 'ausstehend')}
                                  </span>
                                )}
                              </td>
                              <td>
                                <small className="text-muted">
                                  {/* Desktop: Vollständiges Datum mit Uhrzeit */}
                                  <span className="d-none d-md-inline">
                                    {new Date(doc.upload_date || doc.uploaded_at).toLocaleString('de-DE')}
                                  </span>
                                  {/* Mobile: Verkürztes Datum TT.MM.YY */}
                                  <span className="d-md-none">
                                    {new Date(doc.upload_date || doc.uploaded_at).toLocaleDateString('de-DE', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: '2-digit'
                                    })}
                                  </span>
                                </small>
                              </td>
                              <td>
                                {/* Desktop: Button-Gruppe */}
                                <div className="btn-group btn-group-sm d-none d-md-flex">
                                  <button
                                    className="btn btn-outline-primary"
                                    onClick={() => handleViewDocument(doc.id, doc.user_id)}
                                    title="Dokument anzeigen"
                                  >
                                    <i className="bi bi-eye-fill"></i>
                                  </button>
                                  {user.role === 'admin' && (
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => handleDelete(doc.id, doc.original_name || doc.filename)}
                                      title="Dokument löschen"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  )}
                                </div>
                                
                                {/* Mobile: Für Mitarbeiter direkter Button, für Admin Dropdown */}
                                {user.role === 'admin' ? (
                                  // Admin: Dropdown-Menü mit mehreren Optionen
                                  <div className="dropdown d-md-none">
                                    <button 
                                      className="btn btn-outline-secondary btn-sm dropdown-toggle" 
                                      type="button" 
                                      data-bs-toggle="dropdown"
                                      title="Aktionen"
                                    >
                                      <i className="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul className="dropdown-menu">
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() => handleViewDocument(doc.id, doc.user_id)}
                                        >
                                          <i className="bi bi-eye-fill me-2"></i>
                                          {t('doc.view')}
                                        </button>
                                      </li>
                                      <li>
                                        <button
                                          className="dropdown-item text-danger"
                                          onClick={() => handleDelete(doc.id, doc.original_name || doc.filename)}
                                        >
                                          <i className="bi bi-trash me-2"></i>
                                          {t('doc.deleteBtn')}
                                        </button>
                                      </li>
                                    </ul>
                                  </div>
                                ) : (
                                  // Mitarbeiter: Nur direkter Anzeigen-Button
                                  <button
                                    className="btn btn-outline-primary btn-sm d-md-none"
                                    onClick={() => handleViewDocument(doc.id, doc.user_id)}
                                    title="Dokument anzeigen"
                                  >
                                    <i className="bi bi-eye-fill"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Contract Template Tab - nur für Admin */}
          {activeTab === 'contract-template' && user.role === 'admin' && (
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  {t('doc.manageTemplate')}
                </h5>
                <p className="text-muted mb-4">
                  {t('doc.manageTemplateDesc')}
                </p>
                
                <DigitalWorkContract
                  isTemplate={true}
                  contractData={contractTemplate}
                  onSave={saveContractTemplate}
                  user={user}
                />
              </div>
            </div>
          )}

          {/* My Contract Tab - für Mitarbeiter */}
          {activeTab === 'my-contract' && user.role !== 'admin' && (
            <div className="card">
              <div className="card-body">
                
                {employeeContract && employeeContract.status === 'submitted' && (
                  <div className="alert alert-info mb-4">
                    <i className="bi bi-info-circle me-2"></i>
                    {t('doc.contractSubmittedOn')}{' '}
                    {new Date(employeeContract.submitted_at).toLocaleDateString('de-DE')}
                  </div>
                )}
                
                {employeeContract && employeeContract.status === 'approved' && (
                  <div className="alert alert-success mb-4">
                    <i className="bi bi-check-circle me-2"></i>
                    {t('doc.contractApprovedMsg')}
                  </div>
                )}
                
                {employeeContract && employeeContract.status === 'rejected' && (
                  <div className="alert alert-danger mb-4">
                    <i className="bi bi-x-circle me-2"></i>
                    {t('doc.contractRejectedMsg')}
                    {employeeContract.rejection_reason && (
                      <div className="mt-1 fw-bold">
                        {t('doc.rejectionReason')}: {employeeContract.rejection_reason}
                      </div>
                    )}
                  </div>
                )}
                
                <DigitalWorkContract
                  isTemplate={false}
                  contractData={employeeContract ? employeeContract.contract_data : null}
                  templateData={contractTemplate} // Übergebe die Vorlage als Fallback
                  onSave={saveEmployeeContract}
                  user={user}
                  isEmployee={true}
                />
              </div>
            </div>
          )}

          {/* Submitted Contracts Tab - nur für Admin */}
          {activeTab === 'submitted-contracts' && user.role === 'admin' && (
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-file-earmark-check me-2"></i>
                  {t('doc.submittedContracts')}
                </h5>
                <p className="text-muted mb-3">
                  {t('doc.submittedContractsDesc')}
                </p>

                {/* Arbeitgeber-Unterschrift: einmal hinterlegen, wird bei jeder Genehmigung
                    automatisch mit Datum in den Vertrag gesetzt */}
                <div className="card mb-4 border-secondary">
                  <div className="card-body py-2 d-flex flex-wrap align-items-center gap-3">
                    <strong>✍️ Arbeitgeber-Unterschrift</strong>
                    {employerSig ? (
                      <img src={employerSig} alt="Unterschrift Arbeitgeber" className="border rounded bg-white" style={{ maxHeight: 60 }} />
                    ) : (
                      <span className="text-warning small">noch nicht hinterlegt</span>
                    )}
                    <input type="file" accept="image/*" className="form-control form-control-sm" style={{ maxWidth: 280 }}
                      onChange={handleEmployerSigUpload} title="Unterschrift (mit Stempel) als Bild hochladen" />
                    <small className="text-muted">
                      Wird bei jeder Genehmigung automatisch mit Datum in den Vertrag eingesetzt.
                    </small>
                    <button className="btn btn-sm btn-outline-primary ms-auto" disabled={!!bulkPdfProgress}
                      title="Alle genehmigten Verträge dieses Jahres als PDF (Browser-Layout) neu erzeugen und beim Mitarbeiter ablegen"
                      onClick={regenerateAllPdfs}>
                      {bulkPdfProgress ? `⏳ ${bulkPdfProgress}` : '📑 PDFs aller genehmigten Verträge neu erzeugen'}
                    </button>
                  </div>
                </div>

                {!Array.isArray(submittedContracts) || submittedContracts.length === 0 ? (
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    {t('doc.noContracts')}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>{t('doc.employee')}</th>
                          <th>{t('doc.status')}</th>
                          <th>{t('doc.tabDocuments')}</th>
                          <th>{t('doc.submittedAt')}</th>
                          <th>{t('doc.signatures')}</th>
                          <th>{t('doc.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(submittedContracts) && submittedContracts.map(contract => (
                          <tr key={contract.id}>
                            <td>
                              <strong>{contract.firstname} {contract.lastname}</strong>
                              {contract.year && (
                                <span className={`badge ms-2 ${Number(contract.year) === new Date().getFullYear() ? 'bg-light text-dark border' : 'bg-dark'}`}
                                  title={Number(contract.year) !== new Date().getFullYear() ? 'Achtung: Vertrag aus einem anderen Jahr!' : `Saison ${contract.year}`}>
                                  {contract.year}
                                </span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${contract.status === 'submitted' ? 'bg-warning' :
                                contract.status === 'approved' ? 'bg-success' :
                                contract.status === 'rejected' ? 'bg-danger' : 'bg-secondary'}`}
                                title={contract.status === 'rejected' && contract.rejection_reason
                                  ? `${t('doc.rejectionReason')}: ${contract.rejection_reason}` : undefined}>
                                {contract.status === 'submitted' ? t('doc.statusSubmitted') :
                                 contract.status === 'approved' ? t('doc.statusApproved') :
                                 contract.status === 'draft' ? t('doc.statusDraft') : contract.status}
                              </span>
                              {contract.status === 'rejected' && contract.rejection_reason && (
                                <div className="small text-danger">{contract.rejection_reason}</div>
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                {contract.contract_data?.idCardImage ? (
                                  <span className="badge bg-success" title="Ausweis vorhanden">
                                    <i className="bi bi-person-badge"></i>
                                  </span>
                                ) : (
                                  <span className="badge bg-danger" title="Ausweis fehlt">
                                    <i className="bi bi-person-badge"></i>
                                  </span>
                                )}
                                {contract.contract_data?.employeeNationality && !['Deutschland','Österreich','Belgien','Bulgarien','Kroatien','Zypern','Tschechien','Dänemark','Estland','Finnland','Frankreich','Griechenland','Ungarn','Irland','Italien','Lettland','Litauen','Luxemburg','Malta','Niederlande','Polen','Portugal','Rumänien','Slowakei','Slowenien','Spanien','Schweden'].includes(contract.contract_data.employeeNationality) && (
                                  <>
                                    <span className={`badge ${contract.contract_data?.workPermitDocument ? 'bg-success' : 'bg-danger'}`} title={`Arbeitserlaubnis ${contract.contract_data?.workPermitDocument ? 'vorhanden' : 'fehlt (Nicht-EU)'}`}>
                                      <i className="bi bi-briefcase"></i>
                                    </span>
                                    <span className={`badge ${contract.contract_data?.residencePermitDocument ? 'bg-success' : 'bg-danger'}`} title={`Aufenthaltstitel ${contract.contract_data?.residencePermitDocument ? 'vorhanden' : 'fehlt (Nicht-EU)'}`}>
                                      <i className="bi bi-file-earmark-check"></i>
                                    </span>
                                  </>
                                )}
                              </div>
                            </td>
                            <td>
                              {contract.submitted_at ? 
                                new Date(contract.submitted_at).toLocaleDateString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : '-'}
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                {contract.contract_data?.employeeSignature && (
                                  <span className="badge bg-success" title="Mitarbeiter unterschrieben">
                                    <i className="bi bi-person-check"></i>
                                  </span>
                                )}
                                {contract.contract_data?.parentSignature && (
                                  <span className="badge bg-success" title="Erziehungsberechtigter unterschrieben">
                                    <i className="bi bi-people-fill"></i>
                                  </span>
                                )}
                                {contract.contract_data?.employerSignature && (
                                  <span className="badge bg-primary" title="Arbeitgeber unterschrieben">
                                    <i className="bi bi-building-check"></i>
                                  </span>
                                )}
                                {!contract.contract_data?.employeeSignature && !contract.contract_data?.parentSignature && !contract.contract_data?.employerSignature && (
                                  <span className="badge bg-warning">{t('doc.noSignatures')}</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="btn-group" role="group">
                                <button 
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => viewContract(contract)}
                                  title="Vertrag anzeigen"
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                                {contract.status === 'submitted' && (
                                  <>
                                    <button 
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => approveWithPdf(contract.id)}
                                      title="Genehmigen"
                                    >
                                      <i className="bi bi-check-lg"></i>
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => rejectContractWithReason(contract.id)}
                                      title="Ablehnen"
                                    >
                                      <i className="bi bi-x-lg"></i>
                                    </button>
                                  </>
                                )}
                                {contract.status === 'approved' && (
                                  <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => generateContractPDF(contract.id)}
                                    title="PDF generieren"
                                  >
                                    <i className="bi bi-file-earmark-pdf"></i>
                                  </button>
                                )}
                                <button 
                                  className="btn btn-sm btn-outline-dark ms-1"
                                  onClick={() => deleteEmployeeContract(contract.id)}
                                  title="Endgültig löschen"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contract View Modal */}
      {showContractModal && selectedContract && (
        <div className="modal fade show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  {t('doc.contractTitle')} - {selectedContract.firstname} {selectedContract.lastname}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowContractModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Status-Zeile: nur Bildschirm, nicht im PDF */}
                <div className="row mb-3 no-pdf">
                  <div className="col-md-6">
                    <strong>{t('doc.status')}:</strong>
                    <span className={`badge ms-2 ${selectedContract.status === 'submitted' ? 'bg-warning' : 
                      selectedContract.status === 'approved' ? 'bg-success' : 'bg-secondary'}`}>
                      {selectedContract.status === 'submitted' ? t('doc.statusSubmitted') : 
                       selectedContract.status === 'approved' ? t('doc.statusApproved') : 
                       selectedContract.status === 'draft' ? t('doc.statusDraft') : selectedContract.status}
                    </span>
                  </div>
                  <div className="col-md-6">
                    <strong>{t('doc.submittedAt')}:</strong> {selectedContract.submitted_at ? 
                      new Date(selectedContract.submitted_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '-'}
                  </div>
                </div>
                
                <DigitalWorkContract
                  isTemplate={false}
                  contractData={selectedContract.contract_data}
                  user={user}
                  readonly={true}
                  onPrintPDF={async () => {
                    try {
                      showSuccess(t('doc.pdfGenerating'));
                      await generatePDFFromContract(selectedContract);
                      showSuccess(t('doc.pdfCreated'));
                    } catch (error) {
                      console.error('PDF generation error:', error);
                      showError(t('doc.errorPdfCreation'));
                    }
                  }}
                />
              </div>
              <div className="modal-footer">
                {selectedContract.status === 'submitted' && (
                  <>
                    <button 
                      type="button" 
                      className="btn btn-success"
                      onClick={() => {
                        approveWithPdf(selectedContract.id);
                        setShowContractModal(false);
                      }}
                    >
                      <i className="bi bi-check-lg me-2"></i>{t('doc.approve')}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-danger"
                      onClick={async () => {
                        const done = await rejectContractWithReason(selectedContract.id);
                        if (done) setShowContractModal(false);
                      }}
                    >
                      <i className="bi bi-x-lg me-2"></i>{t('doc.reject')}
                    </button>
                  </>
                )}
                {selectedContract.status === 'approved' && (
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={() => {
                      generateContractPDF(selectedContract.id);
                      setShowContractModal(false);
                    }}
                  >
                    <i className="bi bi-file-earmark-pdf me-2"></i>{t('doc.generatePdf')}
                  </button>
                )}
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowContractModal(false)}
                >
                  {t('doc.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentManagement;
